import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "interface-management app-page app-page--fill" }
const _hoisted_2 = { class: "page-header" }
const _hoisted_3 = { class: "page-actions" }

import { ref, reactive, onMounted, onUnmounted, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Refresh } from '@element-plus/icons-vue'
import DynamicTable from '@/components/DynamicTable.vue'
import { PermissionManagement } from '@/network'
import { useButtonPermission } from '@/utils/buttonPermission'

interface InterfaceItem {
  Id?: string
  Name?: string
  LinkUrl?: string
  Enabled?: boolean
  CreateId?: number
  CreateBy?: string
  CreateTime?: string
  ModifyTime?: string
  IsDeleted?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'jiekou',
  setup(__props) {

const { can } = useButtonPermission('/QuanXianGuanLi/jiekou.vue')

const dynamicTableRef = ref<InstanceType<typeof DynamicTable> | null>(null)
const loading = ref(false)
const saving = ref(false)
const tableData = ref<InterfaceItem[]>([])
const tableModel = ref<Record<string, string> | undefined>(undefined)
const filterParams = ref<any[]>([])

const currentPage = ref(1)
const pageSize = ref(15)
const total = ref(0)

// 表格最大高度
const tableMaxHeight = ref<number>(500)

// 计算表格最大高度
const calculateTableHeight = () => {
  nextTick(() => {
    const windowHeight = window.innerHeight
    tableMaxHeight.value = windowHeight - 280
  })
}

const paginationConfig = reactive({
  show: true,
  page: currentPage.value,
  pageSize: pageSize.value,
  get total() { return total.value },
  pageSizes: [10, 20, 50, 100],
  layout: 'total, sizes, prev, pager, next, jumper',
  small: true
})

// 表头字段中文映射
const columnLabels: Record<string, string> = {
  Name: '接口名称',
  LinkUrl: '接口路径',
  Enabled: '启用状态',
  CreateBy: '创建人',
  CreateTime: '创建时间',
  ModifyTime: '修改时间'
}

const dialogVisible = ref(false)
const dialogMode = ref<'create' | 'edit'>('create')
const formRef = ref()
const formData = reactive<InterfaceItem>({
  Id: undefined,
  Name: '',
  LinkUrl: '',
  Enabled: true
})

const formRules = {
  Name: [{ required: true, message: '请输入接口名称', trigger: 'blur' }],
  LinkUrl: [{ required: true, message: '请输入接口路径', trigger: 'blur' }]
}

const loadData = async () => {
  try {
    loading.value = true
    const res: any = await PermissionManagement.interfaceList(
      { page: currentPage.value, intPageSize: pageSize.value },
      filterParams.value
    )
    if (res && res.success && res.status === 200 && res.response) {
      tableData.value = Array.isArray(res.response.data) ? res.response.data : []
      total.value = res.response.dataCount || tableData.value.length
      if (res.response.model && typeof res.response.model === 'object') {
        tableModel.value = res.response.model
      } else {
        tableModel.value = undefined
      }
    } else {
      tableData.value = []
      total.value = 0
      tableModel.value = undefined
      ElMessage.error(res?.msg || '数据加载失败')
    }
  } catch (error) {
    console.error('加载接口列表失败:', error)
    tableData.value = []
    total.value = 0
    tableModel.value = undefined
    ElMessage.error('加载接口列表失败')
  } finally {
    loading.value = false
  }
}

const handleFilter = (filters: any[]) => {
  filterParams.value = filters
  currentPage.value = 1
  loadData()
}

const handlePageChange = (page: number) => {
  currentPage.value = page
  loadData()
}

const handlePageSizeChange = (size: number) => {
  pageSize.value = size
  currentPage.value = 1
  loadData()
}

const resetForm = () => {
  formData.Id = undefined
  formData.Name = ''
  formData.LinkUrl = ''
  formData.Enabled = true
}

const openCreateDialog = () => {
  dialogMode.value = 'create'
  resetForm()
  dialogVisible.value = true
}

const handleEdit = (row: InterfaceItem) => {
  dialogMode.value = 'edit'
  Object.assign(formData, row)
  dialogVisible.value = true
}

const handleDelete = (row: InterfaceItem) => {
  if (!row.Id) {
    ElMessage.warning('缺少接口ID，无法删除')
    return
  }
  ElMessageBox.confirm('确定要删除该接口吗？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  })
    .then(async () => {
      try {
        const res: any = await PermissionManagement.interfaceDelete({ id: row.Id })
        if (res && res.success && res.status === 200) {
          ElMessage.success('删除成功')
          loadData()
        } else {
          ElMessage.error(res?.msg || '删除失败')
        }
      } catch (error) {
        console.error('删除接口失败:', error)
        ElMessage.error('删除失败')
      }
    })
    .catch(() => {})
}

const handleSubmit = () => {
  formRef.value?.validate(async (valid: boolean) => {
    if (!valid) return
    try {
      saving.value = true
      const isCreate = dialogMode.value === 'create'
      const res: any = isCreate
        ? await PermissionManagement.interfaceCreate({ ...formData })
        : await PermissionManagement.interfaceUpdate({ ...formData })
      if (res && res.success && res.status === 200) {
        ElMessage.success(isCreate ? '新增成功' : '更新成功')
        dialogVisible.value = false
        loadData()
      } else {
        ElMessage.error(res?.msg || (isCreate ? '新增失败' : '更新失败'))
      }
    } catch (error) {
      console.error('保存接口失败:', error)
      ElMessage.error('保存失败')
    } finally {
      saving.value = false
    }
  })
}

onMounted(() => {
  loadData()
  calculateTableHeight()
  window.addEventListener('resize', calculateTableHeight)
})

onUnmounted(() => {
  window.removeEventListener('resize', calculateTableHeight)
})

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_card = _resolveComponent("el-card")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_switch = _resolveComponent("el-switch")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_el_card, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            (_unref(can)('新增'))
              ? (_openBlock(), _createBlock(_component_el_button, {
                  key: 0,
                  type: "primary",
                  icon: _unref(Plus),
                  onClick: openCreateDialog
                }, {
                  default: _withCtx(() => _cache[5] || (_cache[5] = [
                    _createTextVNode("新增")
                  ])),
                  _: 1
                }, 8, ["icon"]))
              : _createCommentVNode("", true),
            (_unref(can)('列表'))
              ? (_openBlock(), _createBlock(_component_el_button, {
                  key: 1,
                  type: "primary",
                  circle: "",
                  size: "small",
                  class: "app-refresh-btn",
                  icon: _unref(Refresh),
                  onClick: loadData,
                  loading: loading.value,
                  title: "刷新"
                }, null, 8, ["icon", "loading"]))
              : _createCommentVNode("", true)
          ])
        ]),
        _createVNode(DynamicTable, {
          ref_key: "dynamicTableRef",
          ref: dynamicTableRef,
          data: tableData.value,
          model: tableModel.value,
          loading: loading.value,
          "max-height": tableMaxHeight.value,
          "show-column-selector": true,
          "show-export": false,
          "show-edit-button": _unref(can)('编辑'),
          "show-delete-button": _unref(can)('删除'),
          pagination: paginationConfig,
          "column-labels": columnLabels,
          onEdit: handleEdit,
          onDelete: handleDelete,
          onFilter: handleFilter,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange
        }, null, 8, ["data", "model", "loading", "max-height", "show-edit-button", "show-delete-button", "pagination"])
      ]),
      _: 1
    }),
    _createVNode(_component_el_dialog, {
      modelValue: dialogVisible.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((dialogVisible).value = $event)),
      title: dialogMode.value === 'create' ? '新增接口' : '修改接口',
      width: "640px",
      "close-on-click-modal": false
    }, {
      footer: _withCtx(() => [
        _createVNode(_component_el_button, {
          onClick: _cache[3] || (_cache[3] = ($event: any) => (dialogVisible.value = false))
        }, {
          default: _withCtx(() => _cache[6] || (_cache[6] = [
            _createTextVNode("取消")
          ])),
          _: 1
        }),
        (_unref(can)('保存'))
          ? (_openBlock(), _createBlock(_component_el_button, {
              key: 0,
              type: "primary",
              loading: saving.value,
              onClick: handleSubmit
            }, {
              default: _withCtx(() => _cache[7] || (_cache[7] = [
                _createTextVNode("保存")
              ])),
              _: 1
            }, 8, ["loading"]))
          : _createCommentVNode("", true)
      ]),
      default: _withCtx(() => [
        _createVNode(_component_el_form, {
          ref_key: "formRef",
          ref: formRef,
          model: formData,
          rules: formRules,
          "label-width": "100px"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_form_item, {
              label: "接口名称",
              prop: "Name"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: formData.Name,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.Name) = $event)),
                  placeholder: "请输入接口名称"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "接口路径",
              prop: "LinkUrl"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: formData.LinkUrl,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.LinkUrl) = $event)),
                  placeholder: "/api/接口管理/列表查询"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "启用",
              prop: "Enabled"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_switch, {
                  modelValue: formData.Enabled,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.Enabled) = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["model"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ]))
}
}

})